import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';
import type { BookingDisplayStatus } from '@elara/domain/booking-status';
import type { RoomSelection } from '@elara/domain/models/schemas/room-schemas';

import { capitalize } from 'lodash';
import React from 'react';

import { formatOccupancy } from '@elara/client-core/lib/occupancy-utils';
import { cn, formatPrice } from '@elara/client-core/lib/utils';
import { bookingDisplayStatusLabel, bookingStatusFlagsOf } from '@elara/domain/booking-status';
import { formatPaymentScheduleLabel, summarizePaymentSchedule } from '@elara/domain/payment-schedule';

import { bookingStatusIcon, bookingStatusLabelColor, resolveBookingDisplayStatus } from '#lib/booking-status.js';
import { getFilenameFromUrl, getFileTypeIcon } from '#lib/file-utils.js';
import { useCopy } from '#lib/use-copy.js';
import { useItineraryState } from '#states/hooks.js';
import { AdvisorNote } from '#ui/AdvisorNote.js';
import { BookingCancelControls } from '#ui/BookingCancelControls.js';
import { FlightCard } from '#ui/cards/FlightCard.js';
import { ImageGalleryWidget } from '#ui/chat/ImageGalleryWidget.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#ui/index.js';
import { DetailRow } from '#ui/itinerary/ItineraryDetailRow.js';
import { SectionDivider } from '#ui/SectionDivider.js';

/**
 * Read-only dialog showing the confirmed booking details for one or more
 * itinerary items. Used by the bookings tab "View booking" affordance —
 * hotel stays pass every booked room, flights pass the single flight item.
 * For editing a booking, see `BookingDetailsDialog`. Manual entries also get
 * "Remove booking" here (`canRemoveBooking`, same gate as mobile's sheet):
 * for an external stay/flight the whole item goes with it
 * (`removingBookingRemovesItem`), and the confirm copy says so.
 */
export function ViewBookingDialog(): React.ReactNode {
  const itineraryState = useItineraryState();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);
  const item = itineraryState.viewBookingItem;
  const isOpen = itineraryState.isViewBookingDialogOpen && !!item;

  if (!isOpen) return null;

  const close = () => {
    setConfirmRemoveOpen(false);
    itineraryState.closeViewBookingDialog();
  };
  const canEdit = item.canEditBooking;
  const canRemove = item.canRemoveBooking;
  const removesItem = item.removingBookingRemovesItem;

  const handleConfirmRemove = async () => {
    setRemoving(true);
    try {
      // `clearBookingDetails` toasts every outcome itself and never throws; on success it also
      // closes this view dialog (and deletes the whole item for an external booking record).
      const succeeded = await itineraryState.clearBookingDetails(item.id);
      if (succeeded) setConfirmRemoveOpen(false);
    } finally {
      setRemoving(false);
    }
  };
  const displayStatus = resolveBookingDisplayStatus(bookingStatusFlagsOf(item));
  const roomMeta =
    item.type === 'room' ? (item.metadata as (RoomSelection & { selectedRateKey?: string }) | undefined) : undefined;
  const roomRate = roomMeta?.productCode
    ? item.roomOption?.getRateByIdentity({ productCode: roomMeta.productCode, occupancy: roomMeta.occupancy })
    : roomMeta?.selectedRateKey
      ? item.roomOption?.getRateByRateKey(roomMeta.selectedRateKey)
      : undefined;
  const description = item.isPlace
    ? capitalize(item.place?.primaryCategory)
    : item.type === 'room'
      ? // Prefer the booked rate name; fall back to `hotelName` (which resolves the
        // catalog hotel, then the denormalized name for an externally-booked stay).
        (roomRate?.displayName ?? item.hotelName)
      : item.hotel?.name;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
        <DialogContent>
          <DialogHeader className="mb-1">
            <DialogTitle className="break-words">{item.displayTitle}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="min-w-0">
            <BookedItemSection item={item} displayStatus={displayStatus} />
          </div>

          {(canEdit || canRemove) && (
            <DialogFooter className="gap-2">
              {canEdit && (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    close();
                    itineraryState.openBookingDialog(item);
                  }}
                >
                  Edit booking
                </Button>
              )}
              {canRemove && (
                <Button variant="outline" className="flex-1" onClick={() => setConfirmRemoveOpen(true)}>
                  Remove booking
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmRemoveDialog
        open={confirmRemoveOpen}
        removesItem={removesItem}
        removing={removing}
        onCancel={() => setConfirmRemoveOpen(false)}
        onConfirm={() => void handleConfirmRemove()}
      />
    </>
  );
}

/**
 * Remove-booking confirmation, rendered as a SIBLING of the view dialog (the codebase's confirm
 * pattern — see `TripSettingsActions`), not nested inside its `DialogContent`, so the two Radix
 * roots layer via their own portals instead of one mounting inside the other's tree.
 */
function ConfirmRemoveDialog({
  open,
  removesItem,
  removing,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  removesItem: boolean;
  removing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactNode {
  return (
    <Dialog open={open} onOpenChange={(nowOpen) => !nowOpen && !removing && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{removesItem ? 'Remove this booking?' : 'Remove these booking details?'}</DialogTitle>
          <DialogDescription>
            {removesItem
              ? 'This booking and its entry will be removed from your itinerary.'
              : 'The booking details you entered will be cleared. This event stays on your itinerary.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" disabled={removing} onClick={onCancel}>
            Keep booking
          </Button>
          <Button variant="destructive" disabled={removing} onClick={onConfirm}>
            {removing ? 'Removing…' : 'Remove booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookedItemSection({
  item,
  displayStatus,
}: {
  item: ItineraryItem;
  displayStatus: BookingDisplayStatus;
}): React.ReactNode {
  const booking = item.booking;
  const copy = useCopy();

  // A hotel stay's dates ARE the booking (check-in/check-out), and a flight gets its own timeline
  // card above, so neither needs a time here — but an activity/place/custom event's date row also has
  // a specific time worth showing, appended as a range (or just the start time when there's no
  // distinct end).
  const eventTimeRange =
    item.type !== 'room' && item.type !== 'flight' && item.formattedStartTime
      ? item.formattedEndTime && item.formattedEndTime !== item.formattedStartTime
        ? `${item.formattedStartTime} – ${item.formattedEndTime}`
        : item.formattedStartTime
      : undefined;

  const lead = booking?.leadGuest;
  const leadName = lead ? [lead.title, lead.firstName, lead.lastName].filter(Boolean).join(' ').trim() : undefined;
  const provider = booking?.bookedProvider?.name ?? booking?.provider;

  const billingAddress = booking?.address
    ? [
        booking.address.street,
        booking.address.city,
        booking.address.stateProvinceCode,
        booking.address.postalCode,
        booking.address.countryCode,
      ]
        .filter(Boolean)
        .join(', ')
    : undefined;

  const total = booking?.priceSnapshot?.totalCost ?? booking?.actualCost ?? item.cost;

  // Cancellation deadline + payment status. Cancellation is centralized on the item model
  // (`ItineraryItemModel`) so this dialog can't drift from the itinerary event detail / room guide —
  // answers "is this still free to cancel?"; the payment summary answers "was my card charged or just
  // held?".
  const cancelDeadlineLabel = item.cancellationDeadlineLabel;
  const cancellationDeadline = item.cancellationDeadline;
  const isFreeCancellation = item.isFreeCancellation;
  const paySummary = booking?.paymentSchedule?.length ? summarizePaymentSchedule(booking.paymentSchedule) : undefined;
  const paymentStatusLabel = paySummary
    ? paySummary.dueNow
      ? `${formatPrice(paySummary.dueNow.amount, paySummary.dueNow.currency)} charged${
          paySummary.dueLater
            ? ` · ${formatPrice(paySummary.dueLater.amount, paySummary.dueLater.currency)} due at the property`
            : ''
        }`
      : 'Card held to secure the room — no charge'
    : undefined;

  // Resolve the booked rate so we can name the requested extras and the bed
  // type (both stored as opaque Sabre codes on the booking).
  const meta = item.metadata as (RoomSelection & { selectedRateKey?: string }) | undefined;
  const rate = meta?.productCode
    ? item.roomOption?.getRateByIdentity({ productCode: meta.productCode, occupancy: meta.occupancy })
    : meta?.selectedRateKey
      ? item.roomOption?.getRateByRateKey(meta.selectedRateKey)
      : undefined;

  const occupancy = meta?.occupancy ?? rate?.occupancy ?? item.roomOption?.cheapestRate?.occupancy;
  const occupancyLabel = occupancy ? formatOccupancy(occupancy) : undefined;

  const SCOPE_LABELS: Record<string, string> = {
    per_stay: 'per stay',
    per_night: 'per night',
    per_person: 'per person',
  };
  const extras = (booking?.selectedExtras ?? [])
    .filter((ex) => (ex.quantity ?? 1) > 0)
    .map((ex) => {
      const cat = rate?.extras?.find((e) => e.id === ex.extraId);
      return {
        label: cat?.name ?? ex.extraId,
        quantity: ex.quantity ?? 1,
        amount: cat?.amount,
        currency: cat?.currency,
        scope: cat?.scope ? SCOPE_LABELS[cat.scope] : undefined,
      };
    });

  const bedPreference =
    booking?.bedTypePreference !== undefined
      ? item.roomOption?.bedTypes?.find((b) => b.sabreBedTypeCode === booking.bedTypePreference)?.description
      : undefined;

  const attachments = (item.attachments ?? [])
    .filter((a): a is NonNullable<typeof a> => a != null)
    .map((a) => (typeof a === 'string' ? { url: a, name: getFilenameFromUrl(a) } : a));

  // An externally-booked stay has no RoomOption to show rooms from, so it falls back to the stay's own
  // imagery (catalog hotel, else the attached Google Place) rather than rendering no gallery at all.
  const roomImages = item.roomOption?.images?.length ? item.roomOption.images : item.stayImages;
  const placeImages = item.isPlace ? (item.place?.allImages ?? []) : [];

  return (
    <section className="py-4 first:pt-0 last:pb-0 space-y-6 min-w-0">
      {item.type === 'flight' && item.flightOption && (
        <>
          <FlightCard flightOption={item.flightOption} size="lg" className="p-0" displayPricing={false} />
        </>
      )}

      {roomImages && roomImages.length > 0 && (
        <ImageGalleryWidget images={roomImages} aspect="video" round="md" maxImages={3} autoplay={false} />
      )}

      {placeImages.length > 0 && (
        <ImageGalleryWidget images={placeImages} aspect="video" round="md" maxImages={3} autoplay={false} />
      )}

      {item.booking?.advisorNote && <AdvisorNote text={item.booking.advisorNote} />}

      {/* Cancellation-in-progress framing (ELARA-872): the state is transient — the reconciler
          re-verifies with Sabre and resolves it — but while it lasts, say so explicitly instead of
          letting the booking read as active. */}
      {item.isCancellationPending && (
        <div className="rounded-md border bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground">
          We're confirming this cancellation with the hotel system. It usually resolves shortly — no further action is
          needed.
        </div>
      )}

      <div>
        <SectionDivider label="Booking details" className="mb-1" />
        <div className="flex flex-col text-sm">
          <DetailRow
            icon={bookingStatusIcon(displayStatus)}
            iconClassName={bookingStatusLabelColor(displayStatus)}
            value={
              <span className={bookingStatusLabelColor(displayStatus)}>{bookingDisplayStatusLabel(displayStatus)}</span>
            }
          />
          {item.type !== 'flight' && item.formattedStayDateRange && (
            <DetailRow
              icon="event"
              value={
                eventTimeRange ? `${item.formattedStayDateRange} · ${eventTimeRange}` : item.formattedStayDateRange
              }
            />
          )}
          {item.type === 'room' && item.hotel?.checkInTime && (
            <DetailRow icon="login" secondary="Check-in" value={item.hotel.checkInTime} />
          )}
          {item.type === 'room' && item.hotel?.checkOutTime && (
            <DetailRow icon="logout" secondary="Check-out" value={item.hotel.checkOutTime} />
          )}
          {occupancyLabel && <DetailRow icon="group" value={occupancyLabel} />}
          {booking?.confirmationNumber && (
            <DetailRow
              icon="confirmation_number"
              trailingIcon="content_copy"
              secondary="Confirmation number"
              value={booking.confirmationNumber}
              onClick={() => copy(booking.confirmationNumber!, 'Confirmation number copied')}
              tooltip="Copy confirmation number"
              className="rounded"
            />
          )}
          {booking?.reference && (
            <DetailRow
              icon="tag"
              trailingIcon="content_copy"
              secondary="Booking reference"
              value={booking.reference}
              onClick={() => copy(booking.reference!, 'Reference copied')}
              tooltip="Copy reference"
              className="rounded"
            />
          )}

          {lead?.loyaltyId && <DetailRow icon="loyalty" secondary="Loyalty ID" value={lead.loyaltyId} />}

          {bedPreference && <DetailRow icon="hotel" secondary="Bed preference" value={bedPreference} />}
          {item.type === 'room' && booking?.customerRequest && (
            <DetailRow icon="edit_note" secondary="Special requests" value={booking.customerRequest} />
          )}
          {paymentStatusLabel && !booking?.paymentSchedule?.length && (
            <DetailRow icon="credit_card" secondary="Payment" value={paymentStatusLabel} />
          )}
          {(cancelDeadlineLabel || item.cancellationPolicyText) && (
            <DetailRow
              icon={cancellationDeadline ? (isFreeCancellation ? 'currency_exchange' : 'money_off') : 'event_busy'}
              iconClassName={isFreeCancellation ? 'text-success' : undefined}
              value={
                <div className="space-y-0.5">
                  {cancelDeadlineLabel && (
                    <div className={cn(isFreeCancellation && 'text-success')}>{cancelDeadlineLabel}</div>
                  )}
                  {item.cancellationPolicyText && (
                    <div className="text-muted-foreground">{item.cancellationPolicyText}</div>
                  )}
                </div>
              }
            />
          )}
          {total && !booking?.paymentSchedule?.length && (
            <DetailRow icon="payments" secondary="Total cost" value={formatPrice(total.amount, total.currency)} />
          )}
        </div>
      </div>

      {(lead || leadName || billingAddress) && (
        <div>
          <SectionDivider label="Lead guest" className="mb-1" />
          <div className="flex flex-col text-sm">
            {leadName && <DetailRow icon="person" value={leadName} />}
            {lead?.email && <DetailRow icon="mail" value={lead.email} />}
            {lead?.phone && <DetailRow icon="call" value={lead.phone} />}
            {booking?.contactPhone && booking.contactPhone !== lead?.phone && (
              <DetailRow icon="call" secondary="Contact phone" value={booking.contactPhone} />
            )}
            {billingAddress && <DetailRow icon="home" value={billingAddress} />}
          </div>
        </div>
      )}

      {item.type !== 'room' && booking?.customerRequest && (
        <>
          <div>
            <SectionDivider label="Notes" className="mb-4" />
            <div className="text-sm">{booking.customerRequest}</div>
          </div>
        </>
      )}

      {total && booking?.paymentSchedule && booking.paymentSchedule.length > 0 && (
        <>
          <div>
            <SectionDivider label="Payment schedule" className="mb-4" />
            <ul className="space-y-1 text-sm">
              {booking.paymentSchedule.map((entry, i) => (
                <li key={i} className="flex items-baseline justify-between gap-2">
                  <span>
                    {entry.kind === 'at_booking' && item.booking?.status === 'booked'
                      ? 'Paid'
                      : formatPaymentScheduleLabel(entry)}
                  </span>
                  <span className="tabular-nums">{formatPrice(entry.amount, entry.currency)}</span>
                </li>
              ))}

              {booking.paymentSchedule.length > 1 && (
                <li key="total" className="flex items-baseline justify-between gap-2 font-medium">
                  <span>Total</span>
                  <span className="tabular-nums">{formatPrice(total.amount, total.currency)}</span>
                </li>
              )}
            </ul>
          </div>
        </>
      )}

      {extras.length > 0 && (
        <>
          <div>
            <SectionDivider label="Extras" className="mb-4" />
            <ul className="space-y-1 text-sm">
              {extras.map((extra, i) => (
                <li key={i} className="flex items-baseline justify-between gap-2">
                  <span>
                    {extra.label}
                    {` × ${extra.quantity}`}
                  </span>
                  {extra.amount != null && (
                    <span className="tabular-nums whitespace-nowrap">
                      {extra.amount === 0
                        ? 'Free'
                        : formatPrice(extra.amount * extra.quantity, extra.currency ?? 'USD')}
                      {extra.scope ? ` ${extra.scope}` : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground mt-2">Extras are paid directly at the property.</p>
          </div>
        </>
      )}

      {attachments.length > 0 && (
        <div>
          <SectionDivider label="Documents" className="mb-1" />
          <div className="flex flex-col text-sm">
            {attachments.map((attachment, i) => {
              const { iconName, className: iconClassName } = getFileTypeIcon(
                'mimeType' in attachment ? attachment.mimeType : undefined,
                attachment.name,
              );
              return (
                <DetailRow
                  key={i}
                  icon={iconName}
                  iconClassName={iconClassName}
                  value={attachment.name}
                  onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
                  tooltip="Open document"
                  className="rounded"
                />
              );
            })}
          </div>
        </div>
      )}

      <BookingCancelControls item={item} />
    </section>
  );
}
